import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { UploadCloud, FileText, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal that lets a user attach a clinical PDF to a study and run AI extraction.
 *
 * Props:
 *  - open / onOpenChange
 *  - studyId (required)
 *  - studyDescription (string, optional — for header context)
 *  - onImported(report) — callback fired after successful upload + extraction
 */
export default function ImportReportDialog({
  open, onOpenChange, studyId, studyDescription, onImported, initialFile = null,
}) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const reset = () => { setFile(null); setBusy(false); };

  useEffect(() => {
    if (!open) return;
    if (initialFile) {
      if (!initialFile.name?.toLowerCase().endsWith(".pdf") && initialFile.type !== "application/pdf") {
        setFile(null);
        return;
      }
      if (initialFile.size > 50 * 1024 * 1024) {
        setFile(null);
        return;
      }
      setFile(initialFile);
    } else {
      setFile(null);
    }
  }, [open, initialFile]);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf") && f.type !== "application/pdf") {
      toast.error("Only PDF reports are supported right now");
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error("PDF must be under 50 MB");
      return;
    }
    setFile(f);
  };

  const handleImport = async () => {
    if (!file) { toast.error("Please choose a PDF first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/studies/${studyId}/import-report`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000, // up to 3 min for slow PDFs
      });
      const report = r.data;
      if (report.extract_status === "failed") {
        toast.error(`Imported but extraction failed: ${report.extract_error || "unknown error"}`);
      } else {
        const conf = typeof report.extracted?.confidence === "number"
          ? `${Math.round(report.extracted.confidence * 100)}%`
          : "?";
        toast.success(`Imported · AI confidence ${conf}`);
      }
      onImported?.(report);
      onOpenChange(false);
      reset();
      // Auto-navigate so users can review extracted fields immediately.
      navigate(`/reports/${report.id}`, {
        state: { from: `${location.pathname}${location.search}` },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    const f = e.dataTransfer?.files?.[0];
    handleFile(f);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) reset(); } }}
    >
      <DialogContent className="sm:max-w-[480px]" data-testid="import-report-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            Import Clinical Report
          </DialogTitle>
          <DialogDescription className="text-xs">
            Attach a PDF (e.g. Medtronic / 3mensio TAVR planning report). XLSync will extract structured fields with Gemini 2.5 Flash so you can review and edit before saving.
            {studyDescription ? <span className="block mt-1 italic">For study: {studyDescription}</span> : null}
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:bg-secondary/40 transition-colors"
          data-testid="report-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="report-file-input"
          />
          {file ? (
            <div className="flex items-center justify-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-[var(--xl-primary)]" />
              <span className="font-medium">{file.name}</span>
              <span className="text-xs text-muted-foreground">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </span>
            </div>
          ) : (
            <div className="space-y-2">
              <UploadCloud className="w-8 h-8 mx-auto text-muted-foreground" />
              <div className="text-sm">Drop PDF here, or click to browse</div>
              <div className="text-[11px] text-muted-foreground">PDF · up to 50 MB</div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="import-report-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!file || busy}
            className="bg-[var(--xl-primary)] hover:bg-[var(--xl-primary-hover)]"
            data-testid="import-report-submit"
          >
            {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Extracting…</> : "Import & Extract"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
